/**
 * useRoute Hook
 *
 * Custom hook for fetching a single route from Firestore with real-time updates.
 */

import { useState, useEffect, useCallback } from 'react';
import { doc, onSnapshot, getDoc, type DocumentData, type Unsubscribe } from 'firebase/firestore';
import { getFirebaseDb } from '@/lib/firebase';
import type { Route, RouteIdentification, VGrade } from '@flash/shared';
import { normalizeRouteStatus } from '@flash/shared';

/**
 * Convert Firestore document to Route type
 */
const docToRoute = (docData: DocumentData): Route => {
  const route: Route = {
    id: docData['id'] as string,
    gymId: docData['gymId'] as string,
    name: docData['name'] as string,
    officialGrade: (docData['officialGrade'] as VGrade | null) ?? null,
    colorGrade: docData['colorGrade'] as string,
    wallSection: docData['wallSection'] as string,
    description: (docData['description'] as string | null) ?? null,
    position: (docData['position'] as Route['position']) ?? null,
    styles: (docData['styles'] as Route['styles']) ?? [],
    photos: docData['photos'] as Route['photos'],
    setters: (docData['setters'] as Route['setters']) ?? [],
    assignedSetterId: (docData['assignedSetterId'] as string | null) ?? null,
    setterId: docData['setterId'] as string,
    setterName: docData['setterName'] as string,
    setDate: docData['setDate'],
    status: normalizeRouteStatus(docData['status'] ?? 'published'),
    retiredAt: docData['retiredAt'] ?? null,
    createdAt: docData['createdAt'],
    updatedAt: docData['updatedAt'],
    consensusGrade: (docData['consensusGrade'] as VGrade | null) ?? null,
    gradeSubmissionCount: (docData['gradeSubmissionCount'] as number) ?? 0,
    averageRating: (docData['averageRating'] as number | null) ?? null,
    ratingCount: (docData['ratingCount'] as number) ?? 0,
    totalSends: (docData['totalSends'] as number) ?? 0,
    totalAttempts: (docData['totalAttempts'] as number) ?? 0,
    mapPosition: (docData['mapPosition'] as { x: number; y: number } | null) ?? null,
  };

  // Add identification if present (exactOptionalPropertyTypes requires this pattern)
  const identification = docData['identification'] as RouteIdentification | undefined;
  if (identification) {
    route.identification = identification;
  }

  return route;
};

/**
 * State interface for single route query
 */
interface UseRouteState {
  /** The route document (null if not found) */
  route: Route | null;
  /** Whether data is being loaded */
  loading: boolean;
  /** Error message if query failed */
  error: string | null;
}

/**
 * Return type for useRoute hook
 */
interface UseRouteResult extends UseRouteState {
  /** Refetch route from Firestore (for non-realtime mode) */
  refetch: () => Promise<void>;
  /** Whether the route is retired */
  isRetired: boolean;
}

/**
 * Hook to fetch a single route by ID with real-time updates
 *
 * @param routeId - The route document ID to fetch
 * @param options - Optional configuration
 * @param options.realtime - Whether to use real-time listener (default: true)
 * @returns Object containing route, loading state, error, and utility functions
 *
 * @example
 * ```tsx
 * const { route, loading, error, isRetired } = useRoute(routeId);
 *
 * if (loading) return <LoadingSpinner />;
 * if (error) return <ErrorMessage message={error} />;
 * if (!route) return <NotFound />;
 *
 * return (
 *   <div>
 *     {isRetired && <RetiredBanner />}
 *     <RouteDetail route={route} />
 *   </div>
 * );
 * ```
 */
export const useRoute = (
  routeId: string | undefined,
  options: { realtime?: boolean } = {}
): UseRouteResult => {
  const { realtime = true } = options;

  const [state, setState] = useState<UseRouteState>({
    route: null,
    loading: true,
    error: null,
  });

  /**
   * Fetch route from Firestore (one-time)
   */
  const fetchRoute = useCallback(async (): Promise<void> => {
    if (!routeId) {
      setState({ route: null, loading: false, error: 'No route ID provided' });
      return;
    }

    setState((prev) => ({ ...prev, loading: true, error: null }));

    try {
      const db = getFirebaseDb();
      const routeRef = doc(db, 'routes', routeId);
      const routeSnapshot = await getDoc(routeRef);

      if (!routeSnapshot.exists()) {
        setState({ route: null, loading: false, error: null });
        return;
      }

      const route = docToRoute(routeSnapshot.data());
      setState({ route, loading: false, error: null });
    } catch (err) {
      const errorMessage = err instanceof Error ? err.message : 'Failed to fetch route';
      console.error('Error fetching route:', err);
      setState((prev) => ({ ...prev, loading: false, error: errorMessage }));
    }
  }, [routeId]);

  // Real-time subscription effect
  useEffect(() => {
    if (!routeId) {
      setState({ route: null, loading: false, error: 'No route ID provided' });
      return;
    }

    // For non-realtime mode, just fetch once
    if (!realtime) {
      fetchRoute();
      return;
    }

    setState((prev) => ({ ...prev, loading: true, error: null }));

    const db = getFirebaseDb();
    const routeRef = doc(db, 'routes', routeId);

    // Set up real-time listener
    const unsubscribe: Unsubscribe = onSnapshot(
      routeRef,
      (docSnapshot) => {
        if (!docSnapshot.exists()) {
          setState({ route: null, loading: false, error: null });
          return;
        }

        const route = docToRoute(docSnapshot.data());
        setState({ route, loading: false, error: null });
      },
      (err) => {
        const errorMessage = err instanceof Error ? err.message : 'Failed to fetch route';
        console.error('Error in route subscription:', err);
        setState((prev) => ({ ...prev, loading: false, error: errorMessage }));
      }
    );

    return () => {
      unsubscribe();
    };
  }, [routeId, realtime, fetchRoute]);

  /**
   * Whether the route is retired
   */
  const isRetired = state.route?.status === 'retired';

  return {
    ...state,
    refetch: fetchRoute,
    isRetired,
  };
};

export default useRoute;
